import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Badge,
  ProgressBar,
  Spinner,
  Alert,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  getFirestore,
  doc,
  getDoc,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaChartBar,
  FaArrowLeft,
  FaTrophy,
  FaUsers,
  FaStar,
  FaFileExport,
  FaChartLine,
  FaClipboardList,
} from "react-icons/fa";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const ResultadosPage = ({ usuario }) => {
  const { campanaid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campana, setCampana] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [promediosPorDimension, setPromediosPorDimension] = useState([]);
  const [topEvaluados, setTopEvaluados] = useState([]);

  useEffect(() => {
    if (usuario?.ruc && campanaid) {
      cargarResultados();
    }
  }, [usuario, campanaid]);

  const cargarResultados = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";

      // Cargar campaña
      const campanaRef = doc(
        db,
        `evaluaciones/${rucEmpresa}/campañas`,
        campanaid
      );
      const campanaSnap = await getDoc(campanaRef);

      if (!campanaSnap.exists()) {
        console.error("Campaña no encontrada");
        navigate("/evaluaciones");
        return;
      }

      const campanaData = {
        id: campanaSnap.id,
        ...campanaSnap.data(),
      };

      setCampana(campanaData);

      // Cargar resultados
      const resultadosRef = collection(
        db,
        `evaluaciones/${rucEmpresa}/campañas/${campanaid}/resultados`
      );
      const resultadosSnap = await getDocs(resultadosRef);
      const resultadosData = [];

      for (let docSnap of resultadosSnap.docs) {
        const data = docSnap.data();

        // Cargar datos del usuario
        const userRef = doc(db, "users", docSnap.id);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};

        resultadosData.push({
          dni: docSnap.id,
          name: userData.name || "",
          apellidos: userData.apellidos || "",
          cargo: userData.cargo || "",
          ...data,
        });
      }

      setResultados(resultadosData);

      // Calcular promedios por dimensión
      if (campanaData.dimensiones && resultadosData.length > 0) {
        const promedios = campanaData.dimensiones.map((dim) => {
          const sumaPromedios = resultadosData.reduce((sum, resultado) => {
            const promedioDimension =
              resultado.promediosPorDimension?.[dim.nombre] || 0;
            return sum + promedioDimension;
          }, 0);

          const promedio =
            resultadosData.length > 0
              ? sumaPromedios / resultadosData.length
              : 0;

          return {
            dimension: dim.nombre,
            promedio: promedio,
            peso: dim.peso,
          };
        });

        setPromediosPorDimension(promedios);
      }

      // Top 5 evaluados
      const top = resultadosData
        .sort((a, b) => (b.promedioGeneral || 0) - (a.promedioGeneral || 0))
        .slice(0, 5);

      setTopEvaluados(top);
    } catch (error) {
      console.error("Error al cargar resultados:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportarResultados = () => {
    // Aquí se puede implementar la exportación a Excel/PDF
    alert("Función de exportación en desarrollo");
  };

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 fw-semibold">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  if (!campana) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Alert variant="danger">Campaña no encontrada</Alert>
      </div>
    );
  }

  const promedioGeneralTotal =
    resultados.length > 0
      ? resultados.reduce((sum, r) => sum + (r.promedioGeneral || 0), 0) /
        resultados.length
      : 0;

  const getTipoLabel = (tipo) => {
    const tipos = { "90": "90°", "180": "180°", "360": "360°" };
    return tipos[tipo] || tipo;
  };

  const getColorByScore = (score) => {
    if (score >= 4.5) return "#28a745"; // Verde
    if (score >= 3.5) return "#20c997"; // Turquesa
    if (score >= 2.5) return "#ffc107"; // Amarillo
    if (score >= 1.5) return "#fd7e14"; // Naranja
    return "#dc3545"; // Rojo
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        padding: "20px 0",
        width: "100vw",
        maxWidth: "100%",
        minHeight: "100vh",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden" }}
        >
          {/* Header */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderBottom: "3px solid #ff8a80",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaChartBar className="me-3" />
                Resultados - {campana.nombre}
              </h2>
              <div className="d-flex justify-content-center gap-3 mt-2">
                <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                  {getTipoLabel(campana.tipoEvaluacion)}
                </Badge>
                <Badge bg="info" style={{ fontSize: "0.9rem" }}>
                  {resultados.length} evaluaciones completadas
                </Badge>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Botones de acción */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-3">
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 p-3 text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          color: "white",
                        }}
                      >
                        <FaChartLine size={24} />
                      </div>
                      <div>
                        <h5 className="mb-1 fw-semibold">
                          Análisis de Resultados
                        </h5>
                        <p className="text-muted mb-0 small">
                          Visualiza el desempeño consolidado de la campaña
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Exportar resultados</Tooltip>}
                      >
                        <Button
                          className="d-flex align-items-center gap-2 text-white"
                          onClick={exportarResultados}
                          style={{
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                            border: "none",
                          }}
                        >
                          <FaFileExport size="16px" />
                          Exportar
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Volver a la campaña</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={() =>
                            navigate(`/evaluaciones/campana/${campanaid}`)
                          }
                          style={{ borderRadius: "10px" }}
                        >
                          <FaArrowLeft size="16px" />
                          Volver
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Mensaje cuando no hay resultados */}
            {resultados.length === 0 && (
              <Alert variant="info" className="text-center py-5">
                <FaChartBar size={48} className="mb-3" />
                <h5>No hay resultados disponibles</h5>
                <p className="text-muted mb-0">
                  Los resultados aparecerán aquí cuando se completen las
                  evaluaciones
                </p>
              </Alert>
            )}

            {/* Contenido cuando hay resultados */}
            {resultados.length > 0 && (
              <>
                {/* KPIs principales */}
                <Row className="mb-4">
                  <Col md={4}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #17a2b8 0%, #20c997 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaUsers size={32} className="mb-2" />
                        <h4 className="fw-bold">{resultados.length}</h4>
                        <p className="mb-0">Evaluaciones Completadas</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaStar size={32} className="mb-2" />
                        <h4 className="fw-bold">
                          {promedioGeneralTotal.toFixed(2)}
                        </h4>
                        <p className="mb-0">Promedio General</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaClipboardList size={32} className="mb-2" />
                        <h4 className="fw-bold">
                          {campana.dimensiones?.length || 0}
                        </h4>
                        <p className="mb-0">Dimensiones Evaluadas</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Promedios por dimensión */}
                <Card
                  className="border-0 shadow-sm mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <h5 className="mb-0 fw-semibold">
                      <FaChartBar className="me-2" />
                      Promedios por Dimensión
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    {promediosPorDimension.map((dim, index) => (
                      <div key={index} className="mb-4">
                        <div className="d-flex justify-content-between mb-2">
                          <div>
                            <strong>{dim.dimension}</strong>
                            <Badge bg="secondary" className="ms-2">
                              {dim.peso}%
                            </Badge>
                          </div>
                          <div>
                            <strong
                              style={{ color: getColorByScore(dim.promedio) }}
                            >
                              {dim.promedio.toFixed(2)} / 5.00
                            </strong>
                          </div>
                        </div>
                        <ProgressBar
                          now={(dim.promedio / 5) * 100}
                          variant={
                            dim.promedio >= 4
                              ? "success"
                              : dim.promedio >= 3
                              ? "info"
                              : dim.promedio >= 2
                              ? "warning"
                              : "danger"
                          }
                          style={{ height: "20px" }}
                        />
                      </div>
                    ))}
                  </Card.Body>
                </Card>

                {/* Top 5 Evaluados */}
                <Card
                  className="border-0 shadow-sm mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <h5 className="mb-0 fw-semibold">
                      <FaTrophy className="me-2" />
                      Top 5 Evaluados
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            #
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Nombre
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Cargo
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Promedio
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topEvaluados.map((evaluado, index) => (
                          <tr key={evaluado.dni}>
                            <td className="text-center py-3 px-3">
                              <Badge
                                bg={
                                  index === 0
                                    ? "warning"
                                    : index === 1
                                    ? "secondary"
                                    : "info"
                                }
                                style={{ fontSize: "0.85rem" }}
                              >
                                {index === 0 ? (
                                  <FaTrophy />
                                ) : (
                                  `${index + 1}°`
                                )}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <div>
                                <div className="fw-bold">
                                  {evaluado.apellidos}, {evaluado.name}
                                </div>
                                <small className="text-muted">
                                  {evaluado.dni}
                                </small>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <span className="text-muted">
                                {evaluado.cargo || "N/A"}
                              </span>
                            </td>
                            <td className="text-center py-3 px-3">
                              <h5
                                className="mb-0 fw-bold"
                                style={{
                                  color: getColorByScore(
                                    evaluado.promedioGeneral
                                  ),
                                }}
                              >
                                {(evaluado.promedioGeneral || 0).toFixed(2)}
                              </h5>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                {/* Tabla completa de resultados */}
                <Card
                  className="border-0 shadow-sm mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <h5 className="mb-0 fw-semibold">
                      <FaUsers className="me-2" />
                      Resultados Completos
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                      <Table hover responsive className="mb-0">
                        <thead
                          style={{
                            backgroundColor: "#f8f9fa",
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                          }}
                        >
                          <tr>
                            <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                              #
                            </th>
                            <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                              DNI
                            </th>
                            <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                              Nombre
                            </th>
                            <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                              Cargo
                            </th>
                            <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                              Promedio
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultados
                            .sort(
                              (a, b) =>
                                (b.promedioGeneral || 0) -
                                (a.promedioGeneral || 0)
                            )
                            .map((resultado, index) => (
                              <tr key={resultado.dni}>
                                <td className="text-center py-3 px-3">
                                  <Badge
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                    }}
                                  >
                                    {index + 1}
                                  </Badge>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="fw-semibold">
                                    {resultado.dni}
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <div>
                                    <div className="fw-bold text-dark">
                                      {resultado.apellidos}, {resultado.name}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="text-muted">
                                    {resultado.cargo || "N/A"}
                                  </span>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <h6
                                    className="mb-0 fw-bold"
                                    style={{
                                      color: getColorByScore(
                                        resultado.promedioGeneral
                                      ),
                                    }}
                                  >
                                    {(resultado.promedioGeneral || 0).toFixed(
                                      2
                                    )}
                                  </h6>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ResultadosPage;
